import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import ConfirmModal from '../ConfirmModal';

export default function SectorListsManager({ organizationId, canEditGlobal }) {
  const [sectorLists, setSectorLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newListForm, setNewListForm] = useState({ name: '', description: '' });
  const [showAddSectorForm, setShowAddSectorForm] = useState(null);
  const [newSectorForm, setNewSectorForm] = useState({ name: '', description: '' });
  const [expandedPredefined, setExpandedPredefined] = useState({});
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // { type: 'list' | 'sector', id, name }

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  const loadSectorLists = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getSectorLists(organizationId);
      setSectorLists(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load sector lists');
      console.error('Error loading sector lists:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSectorLists();
  }, [organizationId]);

  const handleCreateList = async (e) => {
    e.preventDefault();
    if (!newListForm.name.trim()) {
      showToast('error', 'Please enter a name');
      return;
    }

    try {
      const payload = {
        name: newListForm.name,
        description: newListForm.description || null,
        organization_id: organizationId,
        sectors: []
      };
      await adminApi.createSectorList(payload);
      showToast('success', 'Sector list created successfully');
      setNewListForm({ name: '', description: '' });
      setShowNewForm(false);
      await loadSectorLists();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to create sector list');
    }
  };

  const handleDeleteList = async (listId, listName) => {
    setItemToDelete({ type: 'list', id: listId, name: listName });
    setConfirmDeleteOpen(true);
  };

  const handleAddSector = async (listId) => {
    if (!newSectorForm.name.trim()) {
      showToast('error', 'Please enter a sector name');
      return;
    }

    try {
      const payload = {
        name: newSectorForm.name,
        description: newSectorForm.description || null
      };
      await adminApi.addSector(listId, payload);
      showToast('success', 'Sector added successfully');
      setNewSectorForm({ name: '', description: '' });
      setShowAddSectorForm(null);
      await loadSectorLists();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to add sector');
    }
  };

  const handleDeleteSector = async (sectorId, sectorName) => {
    setItemToDelete({ type: 'sector', id: sectorId, name: sectorName });
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'list') {
        await adminApi.deleteSectorList(itemToDelete.id);
        showToast('success', 'Sector list deleted successfully');
      } else if (itemToDelete.type === 'sector') {
        await adminApi.deleteSector(itemToDelete.id);
        showToast('success', 'Sector deleted successfully');
      }
      await loadSectorLists();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to delete');
    } finally {
      setConfirmDeleteOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* New Sector List Form */}
      {showNewForm && (
        <div className="bg-eucnet-grey-50 border border-eucnet-grey-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-eucnet-navy mb-3">Create New Sector List</h3>
          <form onSubmit={handleCreateList} className="space-y-3 max-w-md">
            <div>
              <label className="block text-xs font-medium text-eucnet-grey-700 mb-1">Name *</label>
              <input
                type="text"
                value={newListForm.name}
                onChange={(e) => setNewListForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-eucnet-grey-300 rounded px-2 py-1 text-sm"
                placeholder="e.g., Critical Sectors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-eucnet-grey-700 mb-1">Description</label>
              <textarea
                value={newListForm.description}
                onChange={(e) => setNewListForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full border border-eucnet-grey-300 rounded px-2 py-1 text-sm"
                placeholder="Optional description"
                rows="2"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-3 py-1 bg-eucnet-sky text-white text-xs font-medium rounded hover:bg-eucnet-sky/90"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="px-3 py-1 bg-eucnet-grey-200 text-eucnet-grey-700 text-xs font-medium rounded hover:bg-eucnet-grey-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sector Lists Table */}
      {loading ? (
        <div className="text-eucnet-grey-500 py-4">Loading sector lists…</div>
      ) : sectorLists.length === 0 ? (
        <div className="text-center py-8 text-eucnet-grey-600">
          <p className="text-sm mb-3">No sector lists available</p>
          <button
            onClick={() => setShowNewForm(true)}
            className="text-eucnet-sky hover:underline text-sm font-medium"
          >
            Create one now
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Custom Lists Section */}
          {(() => {
            const customLists = sectorLists.filter(l => !l.is_predefined);
            return (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-semibold text-eucnet-navy">Custom Lists</h3>
                  {!showNewForm && (
                    <button
                      onClick={() => setShowNewForm(true)}
                      className="px-4 py-2 bg-eucnet-sky text-white text-sm font-medium rounded hover:bg-eucnet-sky/90"
                    >
                      + Add Custom List
                    </button>
                  )}
                </div>
                {customLists.length === 0 ? (
                  <p className="text-sm text-eucnet-grey-500 py-4">No custom lists yet</p>
                ) : (
                  <div className="space-y-4">
                    {customLists.map((list) => (
                      <div key={list.id} className="bg-white border border-eucnet-grey-200 rounded-lg overflow-hidden">
                        {/* List Header */}
                        <div className="p-4 bg-eucnet-grey-50 border-b border-eucnet-grey-200 flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-eucnet-navy">{list.name}</h3>
                            {list.description && (
                              <p className="text-sm text-eucnet-grey-600 mt-1">{list.description}</p>
                            )}
                            <p className="text-xs text-eucnet-grey-500 mt-1">
                              {list.sectors?.length || 0} sectors
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteList(list.id, list.name)}
                            className="text-red-600 hover:text-red-700 text-xs font-medium hover:underline ml-4"
                          >
                            Delete
                          </button>
                        </div>

                        {/* Sectors List */}
                        <div className="p-4">
                          {list.sectors && list.sectors.length > 0 ? (
                            <div className="space-y-2 mb-4">
                              {list.sectors.map((sector) => (
                                <div key={sector.id} className="flex justify-between items-start text-sm bg-eucnet-grey-50 p-2 rounded">
                                  <div className="flex-1">
                                    <p className="font-medium text-eucnet-navy">{sector.name}</p>
                                    {sector.description && (
                                      <p className="text-xs text-eucnet-grey-600">{sector.description}</p>
                                    )}
                                    {sector.nis2_category && (
                                      <span className="inline-block text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-1">
                                        {sector.nis2_category}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleDeleteSector(sector.id, sector.name)}
                                    className="text-red-600 hover:text-red-700 text-xs font-medium hover:underline ml-2"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-eucnet-grey-500 mb-4">No sectors defined</p>
                          )}

                          {/* Add Sector Form */}
                          {showAddSectorForm === list.id ? (
                            <div className="border-t border-eucnet-grey-200 pt-3">
                              <div className="space-y-2 max-w-md mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-eucnet-grey-700 mb-1">Sector Name *</label>
                                  <input
                                    type="text"
                                    value={newSectorForm.name}
                                    onChange={(e) => setNewSectorForm(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full border border-eucnet-grey-300 rounded px-2 py-1 text-xs"
                                    placeholder="e.g., Energy"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-eucnet-grey-700 mb-1">Description</label>
                                  <input
                                    type="text"
                                    value={newSectorForm.description}
                                    onChange={(e) => setNewSectorForm(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full border border-eucnet-grey-300 rounded px-2 py-1 text-xs"
                                    placeholder="Optional"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAddSector(list.id)}
                                  className="px-3 py-1 bg-eucnet-sky text-white text-xs font-medium rounded hover:bg-eucnet-sky/90"
                                >
                                  Add Sector
                                </button>
                                <button
                                  onClick={() => setShowAddSectorForm(null)}
                                  className="px-3 py-1 bg-eucnet-grey-200 text-eucnet-grey-700 text-xs font-medium rounded hover:bg-eucnet-grey-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowAddSectorForm(list.id)}
                              className="text-eucnet-sky hover:underline text-xs font-medium"
                            >
                              + Add Sector
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Predefined Lists Section - Compact View */}
          {(() => {
            const predefinedLists = sectorLists.filter(l => l.is_predefined);
            if (predefinedLists.length > 0) {
              return (
                <div className="border-t pt-6">
                  <h3 className="text-sm font-semibold text-eucnet-navy mb-3">Reference Lists (Read-only)</h3>
                  <div className="space-y-2">
                    {predefinedLists.map((list) => (
                      <div key={list.id} className="border border-eucnet-grey-200 rounded-lg">
                        <button
                          onClick={() => setExpandedPredefined(prev => ({ ...prev, [list.id]: !prev[list.id] }))}
                          className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-eucnet-grey-50 transition-colors"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-eucnet-navy text-sm">{list.name}</p>
                            <p className="text-xs text-eucnet-grey-500">{list.sectors?.length || 0} sectors</p>
                          </div>
                          <span className="text-eucnet-grey-500 text-lg">
                            {expandedPredefined[list.id] ? '▼' : '▶'}
                          </span>
                        </button>

                        {expandedPredefined[list.id] && (
                          <div className="px-4 py-3 bg-eucnet-grey-50 border-t border-eucnet-grey-200 max-h-64 overflow-y-auto">
                            {list.sectors && list.sectors.length > 0 ? (
                              <div className="grid grid-cols-2 gap-2">
                                {list.sectors.map((sector) => (
                                  <div key={sector.id} className="text-xs">
                                    <p className="font-medium text-eucnet-navy">{sector.name}</p>
                                    {sector.nis2_category && (
                                      <span className="inline-block text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded mt-0.5">
                                        {sector.nis2_category}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-eucnet-grey-500">No sectors defined</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      <ConfirmModal
        open={confirmDeleteOpen}
        title={itemToDelete?.type === 'list' ? 'Delete sector list?' : 'Delete sector?'}
        message={
          itemToDelete?.type === 'list'
            ? `Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`
            : `Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setItemToDelete(null);
        }}
      />
    </div>
  );
}
